import Vue from "vue";
import VueRouter from "vue-router";
import JwtService from "@/common/jwt.service.js";
import { PURGE_AUTH, SET_PERMISSIONS } from "@/store/mutations.type";
import { checkRoles } from "@/role/role";
import goTo from "vuetify/lib/services/goto";
import { GET_ROLE } from "@/api/auth";

Vue.use(VueRouter);

const routes = [
  {
    path: "/login",
    name: "Login",
    component: () => import(/* webpackChunkName: "login" */ "../views/Login"),
    meta: {
      guest: true,
      type: "both",
    },
  },
  {
    path: "/m",
    component: () =>
      import(/* webpackChunkName: "layout_default_m" */ "../layout/Mobile"),
    meta: {
      requiredAuth: true,
      type: "mobile",
    },
    children: [
      {
        path: "personal-target",
        name: "PersonalTargetMobile",
        component: () =>
          import(
            /* webpackChunkName: "personal_target_m" */ "../views/mobile/personal/PersonalTargetMobile"
          ),
      },
    ],
  },
  {
    path: "/",
    component: () =>
      import(/* webpackChunkName: "layout_default" */ "../layout/Default"),
    meta: {
      requiredAuth: true,
      type: "desktop",
    },
    children: [
      {
        path: "",
        name: "Home",
        component: () => import(/* webpackChunkName: "home" */ "../views/Home"),
      },
      {
        path: "target-setting",
        name: "TargetSetting",
        component: () =>
          /* webpackChunkName: "target-setting" */ import(
            "../views/targets/TargetSetting"
          ),
      },
      {
        path: "target-settings",
        name: "ManagerTargetSettings",
        component: () =>
          import(
            /* webpackChunkName: "manager-target-settings" */ "../views/targets/ManagerTargetSettings"
          ),
      },
      {
        path: "personal-target",
        name: "PersonalTarget",
        component: () =>
          /* webpackChunkName: "personalTarget" */ import(
            "../views/personal/PersonalTarget"
          ),
      },
      {
        path: "team-target",
        name: "TeamTarget",
        component: () =>
          /* webpackChunkName: "teamTarget" */ import(
            "../views/team/TeamTarget"
          ),
      },
      {
        path: "team-target-member",
        name: "TeamTarget-member",
        component: () =>
          /* webpackChunkName: "teamTargetPerson" */ import(
            "../views/team/TeamTargetPerson"
          ),
      },
      {
        path: "setting-common",
        name: "SettingCommon",
        component: () =>
          import(/* webpackChunkName: "setting-common" */ "../views/settings"),
      },
      {
        path: "member-transfer-setting",
        name: "MemberTransferSetting",
        component: () =>
          /* webpackChunkName: "member-transfer-setting" */ import(
            "../views/team/MemberTransferSetting.vue"
          ),
      },
      {
        path: "data-collection",
        name: "DataCollection",
        component: () =>
          import(
            /* webpackChunkName: "data-collection" */ "../views/dataCollection/DataCollection.vue"
          ),
      },
      {
        path: "data-collection/:timeline/:id",
        name: "DataCollectionDetail",
        component: () =>
          import(
            /* webpackChunkName: "data-collection-detail" */ "../views/dataCollection/DataCollectionDetail.vue"
          ),
      },
      {
        name: "Error403",
        path: "403",
        component: () =>
          import(
            /* webpackChunkName: "error403" */ "../views/error/Error403.vue"
          ),
      },
      {
        path: "history",
        name: "History",
        component: () =>
          import(/* webpackChunkName: "history" */ "../views/History.vue"),
      },
    ],
  },
  {
    path: "*",
    name: "Error404",
    component: () =>
      import(/* webpackChunkName: "error_404" */ "../views/error/Error404"),
  },
];

const router = new VueRouter({
  mode: "history",
  base: process.env.BASE_URL,
  scrollBehavior: (to, from, savedPosition) => {
    let scrollTo = 0;

    if (to.hash) {
      scrollTo = to.hash;
    } else if (savedPosition) {
      scrollTo = savedPosition.y;
    }
    return goTo(scrollTo);
  },
  routes,
});

router.beforeEach(async (to, from, next) => {
  if (to.matched.some((record) => record.meta.requiredAuth)) {
    if (!JwtService.isLogged()) {
      vx.commit(PURGE_AUTH);
      next({
        path: "/login",
        params: { nextUrl: to.fullPath },
        query: { redirect: to.fullPath },
      });
    } else {
      const roles = (await GET_ROLE()).data;
      vx.commit(SET_PERMISSIONS, roles);
      if (!checkRoles(to.name, roles) && "Error403" !== to.name) {
        next({ path: "/403" });
      } else {
        next();
      }
    }
  } else {
    if (!JwtService.isLogged()) {
      vx.commit(PURGE_AUTH);
    }
    next();
  }
  if (to.matched.some((record) => record.meta.type === "mobile")) {
    if (!Vue.$device.mobile) {
      next({ path: "/personal-target" });
    }
  } else if (to.matched.some((record) => record.meta.type === "desktop")) {
    if (Vue.$device.mobile) {
      next({ name: "PersonalTargetMobile" });
    }
  }
});

export default router;
